<template>
  <div>
    <LayoutCard rounded>
      <div class="tp-text-subtitle">
        {{ $t('components.requestInvoice.form.title') }}
      </div>
      <div class="tp-text-body mt-3">
        {{ $t('components.requestInvoice.form.subtitle') }}
      </div>
    </LayoutCard>
    <v-form ref="form" validate-on="submit" @submit.prevent="handleSubmit">
      <LayoutCard
        rounded
        :title="$t('components.requestInvoice.form.contactDetails')"
      >
        <v-text-field
          v-model="formData.name"
          :disabled="loading"
          color="black"
          :rules="[() => !!formData.name || $t('generic.requiredField')]"
          :label="$t('components.requestInvoice.form.namePlaceholder')"
          :placeholder="$t('components.requestInvoice.form.namePlaceholder')"
          type="text"
          class="py-2 mt-3"
          maxlength="100"
        ></v-text-field>
        <v-text-field
          v-model="formData.email"
          :disabled="loading"
          color="black"
          :rules="[() => !!formData.email || $t('generic.requiredField')]"
          :label="$t('components.requestInvoice.form.emailPlaceholder')"
          :placeholder="$t('components.requestInvoice.form.emailPlaceholder')"
          type="email"
          class="py-2"
          :hint="$t('components.requestInvoice.form.emailHint')"
          persistent-hint
          maxlength="100"
        ></v-text-field>
      </LayoutCard>

      <LayoutCard
        rounded
        :title="$t('components.requestInvoice.form.companyDetails')"
      >
        <v-autocomplete
          v-model="formData.country"
          :items="countries"
          item-text="title"
          item-value="value"
          :disabled="loading || dataFilled"
          color="black"
          item-color="black"
          :rules="[() => !!formData.country || $t('generic.requiredField')]"
          :label="$t('components.requestInvoice.form.countryPlaceholder')"
          :placeholder="$t('components.requestInvoice.form.countryPlaceholder')"
          class="py-2 mt-3"
        />
        <SearchForCompany
          v-if="!showCompanyNameManually"
          :country-code="formData.country"
          @onCompanySelected="fillCompanyData"
          @onCompanyNameManually="resetDataForManualEntry"
        />
        <v-expand-transition>
          <v-text-field
            v-if="showCompanyNameManually"
            v-model="formData.businessName"
            :disabled="loading || dataFilled"
            color="black"
            :rules="[
              () => !!formData.businessName || $t('generic.requiredField'),
            ]"
            :label="
              $t('components.requestInvoice.form.businessNamePlaceholder')
            "
            :placeholder="
              $t('components.requestInvoice.form.businessNamePlaceholder')
            "
            type="text"
            class="py-2"
            maxlength="100"
          >
            <template #append>
              <v-icon
                v-if="dataFilled"
                @click="resetDataForAutomaticEntry"
                class="mr-2"
                color="black"
              >
                mdi-close-circle
              </v-icon>
            </template>
          </v-text-field>
        </v-expand-transition>
        <v-expand-transition>
          <div v-if="showCompanyFields">
            <v-text-field
              v-model="formData.businessCode"
              :disabled="loading || dataFilled"
              color="black"
              :rules="[
                () => !!formData.businessCode || $t('generic.requiredField'),
              ]"
              :label="
                $t('components.requestInvoice.form.businessCodePlaceholder')
              "
              :placeholder="
                $t('components.requestInvoice.form.businessCodePlaceholder')
              "
              type="text"
              class="py-2"
              maxlength="100"
            ></v-text-field>
            <v-text-field
              v-model="formData.businessAddress"
              :disabled="loading"
              color="black"
              :rules="[
                () => !!formData.businessAddress || $t('generic.requiredField'),
              ]"
              :label="
                $t('components.requestInvoice.form.businessAddressPlaceholder')
              "
              :placeholder="
                $t('components.requestInvoice.form.businessAddressPlaceholder')
              "
              type="text"
              class="py-2"
              maxlength="100"
            ></v-text-field>
            <div
              class="d-flex align-center justify-space-between mb-3"
              style="gap: 2em"
            >
              <div class="tp-text-subtitle-semibold">
                {{ $t('components.requestInvoice.form.vatCodeQuestion') }}
              </div>
              <div class="d-flex align-center justify-flex-end py-2">
                <v-switch
                  v-model="formData.enableVatCodeField"
                  :disabled="loading || dataFilled"
                  hide-details
                  :ripple="false"
                  color="success"
                  inset
                  dense
                  class="marginless"
                ></v-switch>
              </div>
            </div>
            <v-text-field
              v-if="formData.enableVatCodeField"
              v-model="formData.vatCode"
              :disabled="loading || dataFilled"
              color="black"
              :rules="[
                () =>
                  (formData.enableVatCodeField && !!formData.vatCode) ||
                  $t('generic.requiredField'),
              ]"
              :label="$t('components.requestInvoice.form.vatCodePlaceholder')"
              :placeholder="
                $t('components.requestInvoice.form.vatCodePlaceholder')
              "
              type="text"
              class="py-2"
              maxlength="100"
            ></v-text-field>
            <v-checkbox
              v-model="formData.saveRequestInvoiceDetails"
              color="black"
              :ripple="false"
              hide-details
              :label="$t('components.requestInvoice.form.saveDetails')"
            />
          </div>
        </v-expand-transition>
        <div
          v-if="error"
          class="text-center mt-3 mb-4 tp-text-caption"
          :style="{ color: 'red' }"
        >
          {{ $t(error) }}
        </div>
      </LayoutCard>
      <LayoutCard v-if="displayVmiFields" rounded>
        <ScanReceiptForm
          ref="scanReceiptForm"
          :initial-receipt-data="initialReceiptData"
          @submit="handleSubmit"
        />
      </LayoutCard>
      <div class="mb-4 mx-10 mt-11">
        <Button dark :disabled="loading" data-cy="request-invoice-submit">
          <v-progress-circular
            v-if="loading"
            indeterminate
            :size="15"
            :width="2"
          ></v-progress-circular>
          <span v-else>{{
            $t('components.requestInvoice.form.submitButton')
          }}</span>
        </Button>
      </div>
    </v-form>
  </div>
</template>

<script>
import Button from '@/components/Button.vue';
import SearchForCompany from '@/components/Invoices/SearchForCompany.vue';
import { CountriesList } from '@/utils/constants';
import LayoutCard from '@/components/LayoutCard.vue';
import ScanReceiptForm from '@/components/Invoices/ScanReceiptForm.vue';

export default {
  name: 'CompanyDataForm',
  components: {
    Button,
    SearchForCompany,
    LayoutCard,
    ScanReceiptForm,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    dataFilled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    preselectedData: {
      type: Object,
      default: null,
    },
    initialReceiptData: {
      type: Object,
      default: null,
    },
    displayVmiFields: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      formData: {
        name: null,
        businessName: null,
        businessCode: null,
        businessAddress: null,
        email: null,
        country: 'LT',
        enableVatCodeField: false,
        vatCode: null,
        saveRequestInvoiceDetails: true,
      },
      enterCompanyNameManually: false,
    };
  },
  computed: {
    showCompanyNameManually() {
      return this.enterCompanyNameManually || this.formData.country !== 'LT';
    },
    showCompanyFields() {
      // For non-LT countries, always show fields
      if (this.formData.country !== 'LT') {
        return true;
      }
      // For LT countries, show fields only when company is selected or manual entry is enabled
      return this.dataFilled || this.enterCompanyNameManually;
    },
    countries() {
      const countries = CountriesList;
      return Object.entries(countries).map(([code, name]) => {
        return {
          title: name,
          value: code,
        };
      });
    },
  },
  watch: {
    preselectedData: {
      handler(newData) {
        if (newData) {
          this.fillPreselectedData(newData);
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleSubmit() {
      const isMainFormValid = this.$refs.form.validate();
      const isReceiptFormValid = this.displayVmiFields
        ? this.$refs.scanReceiptForm?.validate() || false
        : true;

      if (isMainFormValid && isReceiptFormValid) {
        const receiptData =
          this.displayVmiFields && this.$refs.scanReceiptForm
            ? this.$refs.scanReceiptForm.getReceiptData()
            : null;
        this.$emit('submit', {
          formData: { ...this.formData },
          receiptData,
        });
      }
    },
    fillCompanyData(company) {
      this.enterCompanyNameManually = false;
      this.formData.businessName = company.title;
      this.formData.businessCode = company.code;
      this.formData.businessAddress = company.address;
      this.formData.vatCode = company.pvmCode;
      this.formData.enableVatCodeField = !!company.pvmCode;
      this.$emit('update:dataFilled', true);
    },
    resetDataForManualEntry() {
      this.enterCompanyNameManually = true;
      this.$emit('update:dataFilled', false);

      this.formData.businessName = null;
      this.formData.businessCode = null;
      this.formData.businessAddress = null;
      this.formData.vatCode = null;
      this.formData.enableVatCodeField = false;
    },
    resetDataForAutomaticEntry() {
      this.enterCompanyNameManually = false;
      this.$emit('update:dataFilled', false);

      this.formData.businessName = null;
      this.formData.businessCode = null;
      this.formData.businessAddress = null;
      this.formData.vatCode = null;
      this.formData.enableVatCodeField = false;
    },
    fillPreselectedData(data) {
      this.formData.businessName = data.business_name;
      this.formData.businessCode = data.business_code;
      this.formData.businessAddress = data.business_address;
      this.formData.vatCode = data.business_vat_code;
      this.formData.enableVatCodeField = !!data.business_vat_code;
      this.formData.country = data.business_country_code;
      this.formData.name = data.name;
      this.formData.email = data.email;
      this.formData.saveRequestInvoiceDetails = true;
      this.enterCompanyNameManually = true;
      this.$emit('update:dataFilled', true);
    },
    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>

<style scoped lang="scss">
.marginless ::v-deep {
  margin-top: 0;
  .v-input--selection-controls__input {
    width: 40px;
    margin: 0;
  }
}
</style>
